/* eslint-disable react-hooks/exhaustive-deps */
// vendors
import React, { useMemo, useState, useEffect } from 'react';
import { useQuery, useQueryCache } from 'react-query';
import { useRouter } from 'routes';
import { APP_SECTIONS_PATHS } from 'constants/routes';
import { useTranslation, Trans } from 'react-i18next';

import {
  getValuesByKey,
  buildDefaultValues,
  buildGeneralInfoFormFieldsSet,
} from './utils';
import * as Yup from 'yup';

// constants
import { teacherQuery, groupsQuery, getCenterQuery } from '../queryConfig';

// styles
import { Form } from './styles/TeacherProfileForm';

import memberAddedErrorImage from 'images/error.svg';
import memberAddedSuccessImage from '../Staff/Manage/assets/memberadded.svg';
import RendererIf from 'components/RendererIf';
import ResultActionModal from 'components/shared/ResultActionModal/ResultActionModal';
import { StylesImageModalResultsUpdateTeacher } from './styles/Commons';

interface GeneralInfoFormProps {
  centerId?: any;
  teacherData?: any;
  centerName?: string;
  onUpdateInformation?: (data: any) => any;
}

const submitButtonProps = {
  children: <Trans>SAVE</Trans>,
  color: '#75B753',
  enableOnValidForm: true,
};

let formData: any;

const GeneralInfoForm = ({
  centerId,
  teacherData,
  centerName,
}: GeneralInfoFormProps) => {
  const queryCache = useQueryCache();
  const { t: translate } = useTranslation();

  const { getGroups } = groupsQuery;
  const { editTeacher } = teacherQuery;
  const previousGroupList: any = queryCache.getQueryData(getGroups.key) || [];
  const previousCenterList: any =
    queryCache.getQueryData(getCenterQuery.key) || [];
  const [resultActionModal, setResultActionModal] = useState<boolean>(false);
  const [centersSelected, setCentersSelected] = useState<number[]>(
    teacherData?.centers?.map((c: any) => Number(c?.external_id)) || []
  );
  const { navigateTo } = useRouter();

  const formFieldValidations = Yup.object().shape({
    name: Yup.string()
      .trim()
      .min(2, translate('validations:MIN_LENGTH', { amount: 2 }))
      .max(50, translate('validations:MAX_LENGTH', { amount: 50 })),
    lastname: Yup.string()
      .trim()
      .min(2, translate('validations:MIN_LENGTH', { amount: 2 }))
      .max(50, translate('validations:MAX_LENGTH', { amount: 50 })),
    role_alias: Yup.string()
      .required(translate('validations:FIELD_REQUIRED'))
      .trim()
      .max(70, translate('validations:MAX_LENGTH', { amount: 70 })),
  });

  const validateToRedirect = (dataSubmit: any) => {
    if (!dataSubmit?.centers?.find((c: any) => c.id === Number(centerId))?.id) {
      navigateTo(APP_SECTIONS_PATHS.CENTER_SETTINGS, {
        centerId,
        centerName,
      });
    }
  };

  const ConfigResultActionModal = {
    success: {
      title: translate('settings:UPDATED_TEACHER'),
      image: memberAddedSuccessImage,
      configPopsModal: {
        showCloseButton: false,
        onClosedModal: () => {
          setResultActionModal(false);
        },
      },
    },
    error: {
      title: translate('settings:ERROR'),
      image: memberAddedErrorImage,
      configPopsModal: {
        showCloseButton: true,
        onClosedModal: () => {
          setResultActionModal(false);
        },
      },
    },
  };

  const [resultActionModalInfo] = useState<any>(
    ConfigResultActionModal.success,
  );

  const { refetch: setTeacher } = useQuery(
    editTeacher.key,
    () => editTeacher.resource(formData, centerId, teacherData.id),
    editTeacher.config,
  );

  const {
    isError,
    data: groupList = previousGroupList,
    refetch: fetchGroups,
  } = useQuery(
    getGroups.key,
    () => getGroups.resource(centerId),
    getGroups.config,
  );

  const {
    data: centerList = previousCenterList,
    refetch: getCenters,
  } = useQuery(
    getCenterQuery.key,
    () => getCenterQuery.resource(centerId),
    getCenterQuery.config,
  );

  useMemo(() => {
    if (!isError && centerId && groupList.length === 0) {
      fetchGroups();
    }
  }, [centerId]);

  useEffect(() => {
    if (centerList.length === 0) {
      getCenters();
    }
  }, [centerList]);

  const onSubmitForm = (data: any) => {
    const groupsWhitCenters = centerList.flatMap((center: any) =>
    center?.groups?.map((group: any) => ({
      ...group,
      label: group?.name,
      value: group?.id,
      parent_id: center.id,
      parent_externalId: Number(center?.external_id),
      parent: { ...center, ...{ selected: false } },
    }))).filter((g: any) => g?.parent_externalId === centersSelected?.find((v: any) => v === g?.parent_externalId));

    const sendGroups = groupsWhitCenters?.filter((group: any) => group?.id === data?.groups?.find((v: any) => v?.id === group?.id)?.id)

    if (teacherData?.id) {
      formData = {
        user: {
          name: data?.name,
          lastname: data?.lastname,
          birthdate: data?.birthdate,
          role_alias: data?.role_alias,
          group_ids: getValuesByKey(sendGroups),
          center_ids: getValuesByKey(data?.centers),
        },
      };
      if (!teacherData.confirmed) {
        formData.user.email = data.reinvite;
      }
      setResultActionModal(true);
      setTeacher();
      setTimeout(() => {
        setResultActionModal(false);
        validateToRedirect(data);
      }, 1500);
    }
  };

  const beforeSendReinvite = (email: string) => {
    formData = {
      user: { email },
    };
    return setTeacher();
  };

  const hadleOnFormFieldChange = (value: any, currentState: any, name: any) => {
    if (name === 'centers') {
      setCentersSelected(value.map((c: any) => c?.external_id));
    }
  };

  return (
    <>
      <Form
        schema={formFieldValidations}
        onSubmitForm={onSubmitForm}
        submitButtonProps={submitButtonProps}
        onFormFieldChange={hadleOnFormFieldChange}
        defaultValues={buildDefaultValues({
          teacherData,
          groupList,
          centerList,
        })}
        fieldsSetData={buildGeneralInfoFormFieldsSet({
          groupList,
          translate,
          centerName,
          teacherData,
          centerList,
          centersSelected,
          beforeSendReinvite,
        })}
      />
      <RendererIf condition={resultActionModal}>
        <ResultActionModal
          styles={{ Image: StylesImageModalResultsUpdateTeacher }}
          {...resultActionModalInfo}
        />
      </RendererIf>
    </>
  );
};

export default GeneralInfoForm;
